import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { CommunicationStateType, CommunicationsUIStateInbox, MessageOrTaskEditContext } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { IndividualMessageStateDTO } from "@/Communications/Api/Dto/Responses/IndividualMessageStateDTO";
import { LoadMessageTaskViewResponse } from "@/Communications/Api/Dto/Responses/LoadMessageTaskViewResponse";
import { IndividualDTO } from "@/Communications/Models/MessageOrTaskItem";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { MessageTaskViewComponent } from "./MessageTaskViewComponent";

interface IMessageTaskViewContainerProps {
    individualMessageId: string;
    title: string;

    communicationUIState?: CommunicationsUIStateInbox;

    showPatientBanner?: boolean;
    showAddNoteControl?: boolean;

    onRightButtonsClick?: (model: LoadMessageTaskViewResponse, context: MessageOrTaskEditContext) => void;
    onLeftButtonsClick?: () => void;

    /**
     * Raised when a message is marked read.
     */
    onMessageRead?: () => void;

    savedState?: MessageTaskViewContainerState;
    className?: string;
    showFullWidth?: boolean;
}

class MessageTaskViewContainerState {
    public isLoading: boolean;
    public errorMsg: string;

    public messageTaskViewModel: LoadMessageTaskViewResponse;
    public patientBannerModel: IPatientBannerModel;

    public showDeleteControl: boolean;
    public showArchiveControl: boolean;

    public currentFile: AttachedObjectResponse;
}

class MessageTaskViewContainer extends React.Component<IMessageTaskViewContainerProps, MessageTaskViewContainerState> {
    constructor(props: IMessageTaskViewContainerProps) {
        super(props);

        const savedState = props.savedState;
        if (savedState && savedState.messageTaskViewModel.IndividualMessage.Id === props.individualMessageId) {
            this.state = savedState;
        }
        else {
            const initialState = new MessageTaskViewContainerState();
            initialState.isLoading = true;
            initialState.errorMsg = "";
            this.state = initialState;
        }
    }

    public componentDidMount(): void {
        window.setTimeout(() => this.loadModel(this.props.individualMessageId), 10);
    }

    public componentDidUpdate(prevProps: IMessageTaskViewContainerProps): void {
        if (this.props !== prevProps) {
            if (this.props.individualMessageId !== prevProps.individualMessageId)
                this.loadModel(this.props.individualMessageId);
        }

        $(window).trigger("resize");
    }

    private mutateState(mutation: (s: MessageTaskViewContainerState) => void, then?: () => any): void {
        const s = new MessageTaskViewContainerState();
        mutation(s);
        this.setState(s, then);
    }

    public render(): JSX.Element {
        return (
            <div className="workspace">
                {this.state.currentFile &&
                    this.renderAttachmentPreview(this.state.currentFile)
                }

                <MessageTaskViewComponent
                    model={this.state.messageTaskViewModel}

                    title={this.props.title}

                    isLoading={this.state.isLoading}
                    errorMsg={this.state.errorMsg}

                    showPatientBanner={this.props.showPatientBanner && this.state.messageTaskViewModel && this.state.messageTaskViewModel.LinkedPatient != null}
                    showAddNoteControl={this.props.showAddNoteControl}
                    showFooterControls={!!this.props.communicationUIState}
                    showDeleteControl={this.state.showDeleteControl}
                    showArchiveControl={this.state.showArchiveControl}

                    onForwardClicked={() => this.handleForwardClicked()}
                    onReplyClicked={() => this.handleReplyClicked()}
                    onReplyAllClicked={() => this.handleReplyAllClicked()}

                    onDeleteClicked={id => this.handleDeleteClicked(id)}
                    onArchiveClicked={(id, archiveInSharedInbox) => this.handleArchiveClicked(id, archiveInSharedInbox)}
                    onMarkAsUnreadClicked={messageId => this.handleMarkAsUnreadClicked(messageId)}

                    onTaskStatusChanged={statusId => this.handleTaskStatusChanged(statusId)}
                    onTaskGroupTypeChanged={isGroup => this.handleTaskGroupTypeChanged(isGroup)}
                    onNoteAdded={note => this.handleNoteAdded(note)}
                    setCurrentFile={file => this.mutateState(s => s.currentFile = file)}
                    className={this.props.className}
                    showFullWidth={this.props.showFullWidth}
                />
            </div>
        );
    }

    public componentWillUnmount(): void {
        if (this.props.savedState) {
            setTimeout(() => sessionStore.dispatch(
                LocalOperations.Communications.CommunicationStatesSave,
                {
                    type: CommunicationStateType.MessageTaskView,
                    state: this.state
                }
            ), 0);
        }
    }

    private handleForwardClicked(): void {
        this.mutateState(
            s => s.isLoading = false,
            () => this.props.onRightButtonsClick && this.props.onRightButtonsClick(MyleHelper.CloneObject(this.state.messageTaskViewModel), MessageOrTaskEditContext.Forward)
        );
    }

    private handleReplyClicked(): void {
        this.mutateState(
            s => s.isLoading = false,
            () => this.props.onRightButtonsClick && this.props.onRightButtonsClick(MyleHelper.CloneObject(this.state.messageTaskViewModel), MessageOrTaskEditContext.Reply)
        );
    }

    private handleReplyAllClicked(): void {
        this.mutateState(
            s => s.isLoading = false,
            () => this.props.onRightButtonsClick && this.props.onRightButtonsClick(MyleHelper.CloneObject(this.state.messageTaskViewModel), MessageOrTaskEditContext.ReplyAll)
        );
    }

    private handleDeleteClicked(id: string): void {
        if (!id)
            return;

        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                individualMessageId: id,
                isDraftOwner: this.state.messageTaskViewModel.IndividualMessage.IsDraft,
                isTrash: true
            },
            response => this.handleDeletedSuccess(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isLoading = true;
        });
    }

    private handleDeletedSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError(response.Errors);
            return;
        }

        this.mutateState(
            s => s.isLoading = false,
            () => this.props.onLeftButtonsClick && this.props.onLeftButtonsClick()
        );
    }

    private handleArchiveClicked(id: string, archiveInSharedInbox: boolean): void {
        if (!id)
            return;

        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                individualMessageId: id,
                sharedInboxContactIdsToArchive: archiveInSharedInbox && this.state.messageTaskViewModel.SharedInboxContacts ? this.state.messageTaskViewModel.SharedInboxContacts.map(x => x.Id) : null,
                isDraftOwner: this.state.messageTaskViewModel.IndividualMessage.IsDraft,
                isArchive: true
            },
            response => this.handleArchivedSuccess(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isLoading = true;
        });
    }

    private handleArchivedSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError(response.Errors);
            return;
        }

        this.mutateState(
            s => s.isLoading = false,
            () => this.props.onLeftButtonsClick && this.props.onLeftButtonsClick()
        );
    }

    private handleMarkAsUnreadClicked(messageId: string): void {
        if (!messageId)
            return;

        Client.Postv2(
            ServerOperations.Server.Myle_MarkMessagesAsUnRead,
            {
                MessageIds: [messageId]
            },
            () => this.handleMarkAsUnreadSuccess(),
            err => this.onError(err)
        );

        this.mutateState(s => s.isLoading = true);
    }

    private handleMarkAsUnreadSuccess(): void {
        this.mutateState(
            s => s.isLoading = false,
            () => this.props.onLeftButtonsClick && this.props.onLeftButtonsClick()
        );
    }

    private loadModel(id: string): void {
        if (!id) return;

        const requestData = {
            IndividualMessageId: id
        };

        Client.Postv2(
            ServerOperations.Server.Myle_Communications_LoadMessageTaskView,
            requestData,
            response => this.handleLoadMessageTaskViewSuccess(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isLoading = true;
        });
    }

    private handleLoadMessageTaskViewSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError(response.Errors);
            return;
        }

        this.mutateState(
            s => {
                s.isLoading = false;
                s.messageTaskViewModel = response;

                if (this.props.communicationUIState) {
                    s.showArchiveControl = this.props.communicationUIState === CommunicationsUIStateInbox.Inbox;
                    s.showDeleteControl = this.props.communicationUIState != CommunicationsUIStateInbox.Sent
                        && this.props.communicationUIState != CommunicationsUIStateInbox.Trash;
                }
            },
            () => {
                Client.Post(
                    ServerOperations.Server.Myle_MarkMessageAsRead,
                    {
                        IndividualMessageId: this.state.messageTaskViewModel.IndividualMessage.Id
                    },
                    () => this.props.onMessageRead?.()
                );
            }
        );
    }

    private handleTaskStatusChanged(statusId: string): void {
        if (!statusId) return;

        const requestData = {
            IndividualMessageId: this.state.messageTaskViewModel.IndividualMessage.Id,
            CurrentUserId: sessionStore.getCurrentEmployee().ContactId,
            TaskStatusLookupId: statusId
        };

        Client.Postv2(
            ServerOperations.Server.Myle_SetTaskStatus,
            requestData,
            response => this.handleTaskStatusChangedSuccess(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isLoading = true;
        });
    }

    private handleTaskStatusChangedSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError(response.Errors);
            return;
        }

        let imsDTO = this.state.messageTaskViewModel.IndividualMessageState;
        if (response.IndividualMessageState) {
            imsDTO = new IndividualMessageStateDTO();
            imsDTO.Id = response.IndividualMessageState.Id;
            imsDTO.IndividualMessageId = response.IndividualMessageState.IndividualMessageId;
            imsDTO.TaskStatusLookupId = response.IndividualMessageState.TaskStatusLookupId;
            imsDTO.ToContactId = response.IndividualMessageState.ToContactId;
            imsDTO.LastStatusUpdatedDate = response.IndividualMessageState.LastStatusUpdatedDate;
        }

        this.mutateState(
            s => {
                s.isLoading = false;
                s.messageTaskViewModel = this.state.messageTaskViewModel;
                s.messageTaskViewModel.IndividualMessageState = imsDTO;

                if (response.OverallStatusLookupId && response.OverallStatusLookupId !== GuidHelper.Empty) {
                    s.messageTaskViewModel.IndividualMessage.OverallTaskStatusLookupId = response.OverallStatusLookupId;
                }

                let isToClinicianUpdated = false;
                s.messageTaskViewModel.ToClinicians.forEach(x => {
                    if (x.Id === imsDTO.ToContactId) {
                        x.LastStatusUpdatedDate = imsDTO.LastStatusUpdatedDate;
                        x.TaskStatusLookupId = imsDTO.TaskStatusLookupId;
                        isToClinicianUpdated = true;
                    }
                });

                if (!isToClinicianUpdated) {
                    const clinician = new IndividualDTO();
                    clinician.IMS_ID = imsDTO.Id;
                    clinician.LastStatusUpdatedDate = imsDTO.LastStatusUpdatedDate;
                    clinician.TaskStatusLookupId = imsDTO.TaskStatusLookupId;

                    const currentEmployee = cacheStore.GetCurrentEmployee();
                    clinician.Id = currentEmployee.Contact_id;
                    clinician.FirstName = currentEmployee.FirstName;
                    clinician.LastName = currentEmployee.LastName;

                    s.messageTaskViewModel.ToClinicians.push(clinician);
                }
            },
            () => sessionStore.dispatch(LocalPatientOperations.History.Task_LoadPatientTask)
        );
    }

    private handleTaskGroupTypeChanged(isGroup: boolean): void {
        const requestData = {
            IndividualMessageId: this.state.messageTaskViewModel.IndividualMessage.Id,
            IsGroupTask: isGroup
        };

        Client.Postv2(
            ServerOperations.Server.Myle_Task_SetTaskType,
            requestData,
            response => this.handleTaskGroupTypeChangedSuccess(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isLoading = true;
        });
    }

    private handleTaskGroupTypeChangedSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError(response.Errors);
            return;
        }

        this.mutateState(s => {
            s.isLoading = false;
            s.messageTaskViewModel = this.state.messageTaskViewModel;
            s.messageTaskViewModel.IndividualMessage.IsGroupTask = response.IsGroupTask;
        });
    }

    private handleNoteAdded(note: string): void {
        if (!note) return;

        const individualMessageState = this.state.messageTaskViewModel.IndividualMessageState;
        const taskStatusLookupId = (this.state.messageTaskViewModel.ToClinicians || []).filter(p => p.Id == sessionStore.individualContactId).map(p => p.TaskStatusLookupId).firstOrDefault();

        const requestData = {
            MessageOrTaskId: this.state.messageTaskViewModel.IndividualMessage.Id,
            Note: note,
            IndividualMessageStateId: individualMessageState ? individualMessageState.Id : "",
            TaskStatusLookupId: taskStatusLookupId ? taskStatusLookupId : ""
        };

        Client.Postv2(
            ServerOperations.Server.Myle_SaveMessageNote,
            requestData,
            response => this.handleNoteAddedSuccess(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isLoading = true;
        });
    }

    private handleNoteAddedSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError(response.Errors);
            return;
        }

        this.mutateState(s => {
            s.isLoading = false;
            s.messageTaskViewModel = this.state.messageTaskViewModel;
            s.messageTaskViewModel.IndividualMessageNotes.unshift(response.Note);
        });
    }

    private onError(error: ErrorMessage[]): void {
        this.mutateState(s => {
            s.isLoading = false;
            s.errorMsg = error.map(x => x.Message).join("\n");
        });
    }

    private renderAttachmentPreview(attachedObj: AttachedObjectResponse): JSX.Element {
        if (!attachedObj)
            return null;

        this.mutateState(
            s => s.currentFile = null,
            () => ExportableFileHelper.fromAttachedObject(attachedObj).openInNewWindow()
        );
    }
}

export { MessageTaskViewContainer, MessageTaskViewContainerState };
